import { useEffect, useRef, useState } from 'react';

export interface ViewerPhoto {
  url: string;
  cover: boolean;
}

/**
 * ONE PHOTOGRAPH LARGE, AND A WAY THROUGH THE REST (owner review 2026-10-27,
 * points 6 and 7).
 *
 * A location is judged by looking at it, and a 120-pixel thumbnail is not
 * looking. One picture at a time at the panel's full width, a counter so the
 * producer knows how much there is, and three ways through: the arrow
 * buttons, the keyboard's left and right arrows while the viewer has focus,
 * and a horizontal swipe on a touch screen. The strip underneath jumps
 * straight to any of them.
 *
 * LAZY BY DEFAULT. Only the photograph on show is loaded eagerly; the strip's
 * thumbnails load as they scroll into view, so opening a forty-photo gallery
 * does not fetch forty full images.
 */
export function PhotoViewer({ photos, title }: { photos: readonly ViewerPhoto[]; title: string }) {
  const [index, setIndex] = useState(0);
  const touchX = useRef<number | null>(null);
  const strip = useRef<HTMLDivElement | null>(null);

  const count = photos.length;
  const current = photos[Math.min(index, Math.max(count - 1, 0))];

  const go = (next: number) => {
    if (count === 0) return;
    setIndex(((next % count) + count) % count);
  };

  // Keep the thumbnail of the photograph on show in view as the producer
  // moves through a long gallery with the arrows.
  useEffect(() => {
    strip.current
      ?.querySelector<HTMLElement>(`[data-index="${String(index)}"]`)
      ?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [index]);

  if (current === undefined) {
    return (
      <div
        data-testid="photo-viewer-empty"
        style={{
          aspectRatio: '16 / 10',
          display: 'grid',
          placeItems: 'center',
          background: 'var(--color-surface)',
          border: '1px solid var(--color-divider)',
        }}
      >
        <p className="mm" style={{ margin: 0, fontSize: 13 }}>
          This listing has no photographs on the marketplace yet.
        </p>
      </div>
    );
  }

  return (
    // `minWidth: 0` so a flex or grid parent can shrink the viewer below the strip's
    // full width; the strip then scrolls instead of widening whatever holds it.
    <div data-testid="photo-viewer" style={{ minWidth: 0 }}>
      <div
        role="group"
        aria-roledescription="gallery"
        aria-label={`Photographs of ${title}`}
        tabIndex={0}
        data-testid="photo-viewer-stage"
        onKeyDown={(e) => {
          if (e.key === 'ArrowRight') {
            e.preventDefault();
            go(index + 1);
          } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            go(index - 1);
          }
        }}
        onTouchStart={(e) => {
          touchX.current = e.touches[0]?.clientX ?? null;
        }}
        onTouchEnd={(e) => {
          const start = touchX.current;
          const end = e.changedTouches[0]?.clientX;
          touchX.current = null;
          if (start === null || end === undefined) return;
          if (end - start < -40) go(index + 1);
          else if (end - start > 40) go(index - 1);
        }}
        style={{ position: 'relative', background: 'var(--ink)', outlineOffset: 3 }}
      >
        <img
          src={current.url}
          alt={`${title} — photograph ${String(index + 1)} of ${String(count)}`}
          data-testid="photo-viewer-image"
          style={{ width: '100%', aspectRatio: '16 / 10', objectFit: 'contain', display: 'block' }}
        />

        <span
          data-testid="photo-viewer-counter"
          aria-live="polite"
          style={{
            position: 'absolute',
            right: 10,
            bottom: 10,
            background: 'rgba(16, 24, 40, 0.72)',
            color: '#fff',
            fontSize: 12,
            fontWeight: 600,
            padding: '3px 8px',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {`${String(index + 1)} / ${String(count)}`}
        </span>

        {count < 2 ? null : (
          <>
            <button
              type="button"
              className="ctl"
              data-testid="photo-viewer-prev"
              aria-label="Previous photograph"
              onClick={() => {
                go(index - 1);
              }}
              style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', minWidth: 40, minHeight: 40 }}
            >
              ‹
            </button>
            <button
              type="button"
              className="ctl"
              data-testid="photo-viewer-next"
              aria-label="Next photograph"
              onClick={() => {
                go(index + 1);
              }}
              style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', minWidth: 40, minHeight: 40 }}
            >
              ›
            </button>
          </>
        )}
      </div>

      {count < 2 ? null : (
        <div
          ref={strip}
          data-testid="photo-viewer-strip"
          style={{ display: 'flex', gap: 6, overflowX: 'auto', marginTop: 8, paddingBottom: 4 }}
        >
          {photos.map((photo, i) => (
            <button
              key={`${photo.url}-${String(i)}`}
              type="button"
              data-index={i}
              data-testid={`photo-viewer-thumb-${String(i)}`}
              aria-label={`Show photograph ${String(i + 1)}`}
              aria-current={i === index ? 'true' : undefined}
              onClick={() => {
                go(i);
              }}
              style={{
                flex: 'none',
                width: 84,
                padding: 0,
                border: `2px solid ${i === index ? 'var(--color-accent)' : 'transparent'}`,
                background: 'none',
                cursor: 'pointer',
              }}
            >
              <img
                src={photo.url}
                alt=""
                loading="lazy"
                style={{ width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', display: 'block' }}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
